import { AuthStatus } from './auth-core.const';
import { AuthSnapshot, Unsubscribe } from './auth-core.types';

/**
 * An observable snapshot holder with **reference-stable reads**.
 *
 * `getSnapshot()` returns the identical object until something actually
 * changes. That is not a micro-optimisation: it is the contract React's
 * `useSyncExternalStore` requires, and it is precisely what the current
 * provider gets wrong — it rebuilds its context value on every render, so
 * consumers re-render constantly and three separate call sites need `useRef`
 * guards to defend themselves.
 */
export class AuthStore {
  private snapshot: AuthSnapshot;
  private readonly listeners = new Set<(snapshot: AuthSnapshot) => void>();

  constructor(initial?: Partial<AuthSnapshot>) {
    this.snapshot = Object.freeze({
      status: AuthStatus.Idle,
      isAuthenticated: false,
      // Loading until `start()` resolves. A client that has not started knows
      // nothing yet, and reporting "not loading, not authenticated" makes route
      // guards redirect on first paint before auth has been determined.
      isLoading: true,
      subject: null,
      expiresAt: null,
      sourceKind: null,
      error: null,
      endReason: null,
      ...initial,
    });
  }

  getSnapshot(): AuthSnapshot {
    return this.snapshot;
  }

  subscribe(listener: (snapshot: AuthSnapshot) => void): Unsubscribe {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Merges `patch` into the snapshot. No-ops when every field already matches,
   * so redundant writes never wake subscribers.
   */
  patch(patch: Partial<AuthSnapshot>): void {
    const next = { ...this.snapshot, ...patch };
    if (isSameSnapshot(this.snapshot, next)) return;

    this.snapshot = Object.freeze(next);
    this.notify();
  }

  private notify(): void {
    // Copy first: a listener may unsubscribe itself while we are iterating.
    for (const listener of [...this.listeners]) {
      listener(this.snapshot);
    }
  }

  /** Drops all subscribers. Called when the owning client is disposed. */
  clear(): void {
    this.listeners.clear();
  }
}

/** Shallow equality, with `error` compared by code so retries stay quiet. */
function isSameSnapshot(a: AuthSnapshot, b: AuthSnapshot): boolean {
  return (
    a.status === b.status &&
    a.isAuthenticated === b.isAuthenticated &&
    a.isLoading === b.isLoading &&
    a.subject === b.subject &&
    a.expiresAt === b.expiresAt &&
    a.sourceKind === b.sourceKind &&
    a.endReason === b.endReason &&
    a.error?.code === b.error?.code &&
    a.error?.message === b.error?.message
  );
}
